/**
 * A TÍPUS VÁLASZTÓJA A JAVÍTÓ ŰRLAPBAN (#92).
 *
 * Hat típusig GOMBSOR, fölötte szűrős LENYÍLÓ (`docTypeChoice.ts`, Viktor
 * 2026-11-02). Mindkettő ugyanazokat a billentyűket érti: nyíl választ,
 * `Enter` ment (az egész űrlapot), `Esc` elvet.
 *
 * ⚠️ A GOMBSOR EGY `Tab`-MEGÁLLÓ — natív rádiócsoport, a mezőkkel
 * ellentétben. Itt ez a helyes: a választási lehetőségek nem szövegdobozok,
 * és a csoport a képernyőolvasónak is egy kérdés, nem négy.
 */

import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocType } from '@/i18n/generated';
import { docTypeLabel } from '@/i18n/generated';
import { filterTypes, type TypeControl, typeControlFor, typeForDigit } from './docTypeChoice';

interface TypeChoiceProps {
  value: DocType;
  options: readonly DocType[];
  /** Amit az ügyfél mondott — a címke mellett áll, `null`: nem nyilatkozott. */
  clientDocType: DocType | null;
  onChange: (docType: DocType) => void;
  /** Az `Enter` az egész űrlapot menti — a hívó mondja meg, hogyan. */
  onSubmit: () => void;
  /** Csak teszthez: a darabszám helyett kényszerített vezérlő. */
  control?: TypeControl;
}

export function TypeChoice(props: TypeChoiceProps) {
  const { t } = useTranslation();
  const labelId = useId();
  const control = props.control ?? typeControlFor(props.options.length);

  return (
    <div className="correction__type">
      <div className="correction__label-row">
        <span className="correction__label" id={labelId}>
          {t('worklist.correction.label.docType')}
        </span>
        {props.clientDocType === null ? null : (
          <span className="correction__machine">
            {t('worklist.correction.clientSaid', {
              type: docTypeLabel(t, props.clientDocType),
            })}
          </span>
        )}
      </div>
      {control === 'segmented' ? (
        <Segmented {...props} labelId={labelId} />
      ) : (
        <Combobox {...props} labelId={labelId} />
      )}
    </div>
  );
}

function Segmented({
  value,
  options,
  onChange,
  onSubmit,
  labelId,
}: TypeChoiceProps & { labelId: string }) {
  const { t } = useTranslation();
  const name = useId();
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  /**
   * A NYILAKAT A BÖNGÉSZŐ ADJA: a natív rádiócsoport egy `Tab`-megálló, és a
   * nyíl a következőre lép ÉS kiválaszt. Ide csak az kerül, amit a böngésző
   * nem tud: a számbillentyű és az `Enter` mint az ŰRLAP mentése.
   */
  function onKeyDown(event: React.KeyboardEvent<HTMLFieldSetElement>) {
    const digit = typeForDigit(event.key, event, options);
    if (digit !== null) {
      event.preventDefault();
      onChange(digit);
      inputs.current[options.indexOf(digit)]?.focus();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      onSubmit();
    }
  }

  return (
    <fieldset className="correction__segmented" aria-labelledby={labelId} onKeyDown={onKeyDown}>
      {options.map((docType, index) => (
        <label key={docType} className="correction__segment">
          <input
            ref={(element) => {
              inputs.current[index] = element;
            }}
            type="radio"
            name={name}
            className="visually-hidden"
            checked={docType === value}
            onChange={() => onChange(docType)}
          />
          {docTypeLabel(t, docType)}
          <span className="correction__segment-key num" aria-hidden="true">
            {index + 1}
          </span>
        </label>
      ))}
    </fieldset>
  );
}

function Combobox({
  value,
  options,
  onChange,
  onSubmit,
  labelId,
}: TypeChoiceProps & { labelId: string }) {
  const { t } = useTranslation();
  const listId = useId();
  /** `null`: a lista csukva, a mező a választott típust mutatja. */
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(0);

  const open = query !== null;
  const matches = open ? filterTypes(query, options, (docType) => docTypeLabel(t, docType)) : [];

  function openWith(text: string) {
    setQuery(text);
    setActive(0);
  }

  function choose(docType: DocType) {
    onChange(docType);
    setQuery(null);
  }

  function step(direction: 1 | -1) {
    if (!open) {
      openWith('');
      return;
    }
    setActive((current) =>
      matches.length === 0 ? 0 : (current + direction + matches.length) % matches.length
    );
  }

  // NYITOTT LISTÁN az `Enter` választ; CSUKOTTON ment — mint a gombsoron.
  function enter() {
    const chosen = matches[active];
    if (open && chosen !== undefined) {
      choose(chosen);
      return;
    }
    onSubmit();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      step(event.key === 'ArrowDown' ? 1 : -1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      enter();
    } else if (event.key === 'Escape' && open) {
      // ⚠️ AZ `Esc` ELŐBB A LISTÁT ZÁRJA, és csak a csukott mezőn megy tovább
      // az űrlaphoz (elvetés). Különben egy félbehagyott szűrés az egész
      // javítást eldobná.
      event.preventDefault();
      event.stopPropagation();
      setQuery(null);
    }
  }

  const activeId = open && matches[active] !== undefined ? `${listId}-${active}` : undefined;

  return (
    <div className="correction__combobox">
      <input
        role="combobox"
        aria-labelledby={labelId}
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={activeId}
        aria-autocomplete="list"
        className="correction__input"
        value={open ? query : docTypeLabel(t, value)}
        onChange={(event) => openWith(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => setQuery(null)}
      />
      {open ? (
        <div
          className="correction__listbox"
          id={listId}
          role="listbox"
          aria-labelledby={labelId}
          hidden={matches.length === 0}
        >
          {matches.map((docType, index) => (
            <div
              key={docType}
              id={`${listId}-${index}`}
              role="option"
              tabIndex={-1}
              aria-selected={index === active}
              className="correction__option"
              // `mousedown`, NEM `click`: a `click` előtt a mező `blur`-je
              // becsukná a listát, és a kattintás a semmibe menne.
              onMouseDown={(event) => {
                event.preventDefault();
                choose(docType);
              }}
            >
              {docTypeLabel(t, docType)}
            </div>
          ))}
        </div>
      ) : null}
      {/* A „NINCS TALÁLAT" A LISTÁN KÍVÜL: a `listbox` csak opciót birtokolhat,
          egy benne álló mondatot a felolvasó üres listának mondana. */}
      {open && matches.length === 0 ? (
        <output className="correction__listbox-empty">
          {t('worklist.correction.noTypeMatch')}
        </output>
      ) : null}
    </div>
  );
}
